import sdkOverlayImage from '../../assets/sdk-label-overlay.png';
import * as Storage from '../helpers/localStorageHelper';
import { createAppRoute } from '../helpers/routeHelpers';
import { RootState } from '../reducers/reducers';
import { selectAppGroup } from '../selectors/selectors';
import { AppEntity, AppPlatform } from '../types';
import AvatarLink from './AvatarLink';
import AndroidAppIcon from './icons/AndroidAppIcon';
import AndroidTVIcon from './icons/AndroidTVIcon';
import AppleAppIcon from './icons/AppleAppIcon';
import ReactNativeAppIcon from './icons/ReactNativeAppIcon';
import styles from './styles/appAvatarStyles';
import { antGrey } from './styles/globalStyles';
import Avatar from 'antd/lib/avatar/avatar';
import { forEach, groupBy } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import TvOSIcon from './icons/TvOSIcon';
import VisionOSIcon from './icons/VisionOSIcon';
import WearOSIcon from './icons/WearOSIcon';
import Tooltip from 'antd/lib/tooltip';
import { displayTextForAppPlatform } from '@/helpers/displayHelpers';
import { selectAppsInGroup } from '@/selectors/memoizedSelectors';

interface AppGroupIconComponentProps {
    orgIdParam: string;
    orgId: string;
    appGroupId: string;
    selectedAppIdParam?: string;
}

const AppGroupIconComponent: React.FC<AppGroupIconComponentProps> = (props: AppGroupIconComponentProps) => {
    const appGroup = useSelector((state: RootState) => selectAppGroup(state, props.appGroupId));
    const hasMultipleAppsInGroup = appGroup.appIds.length > 1;
    const appsInGroup = useSelector((state: RootState) => selectAppsInGroup(state, props.appGroupId));
    const history = useHistory();

    const useAppImageUrlForApp = React.useMemo(() => {
        const useAppImageUrlForApp: Record<string, boolean> = {};

        const appsByPlatform = groupBy(appsInGroup, (app) => app.appSettings.platform);

        forEach(appsByPlatform, (apps) => {
            if (apps.length > 1) {
                // more than one app in group for the platform, do they have different app store types?
                const appsByAppStoreType = groupBy(apps, (app) => app.appSettings.appStoreType);

                forEach(appsByAppStoreType, (apps) => {
                    if (apps.length > 1) {
                        // apps that have the same platform and the same app store type should have appImageUrls displayed if it's present
                        forEach(apps, (app) => {
                            if (app.appSettings.appImageUrl) {
                                useAppImageUrlForApp[app.id] = true;
                            }
                        });
                    }
                });
            }
        });

        return useAppImageUrlForApp;
    }, [appsInGroup]);

    const selectedApp = appsInGroup.find((app) => app.appId === props.selectedAppIdParam);
    const appGroupContainsSelectedApp = !!selectedApp;
    const isSDKGroup = appsInGroup
        .map((app) => app.appSettings.platform)
        .some((platform) => {
            return platform === AppPlatform.AndroidSDK || platform === AppPlatform.IosSDK;
        });
    const initialStateShowAllApps = appGroupContainsSelectedApp;

    const [showAllApps, setShowAllApps] = React.useState(initialStateShowAllApps);

    const navigateToAppLink = (appIdParam: string, appLink: string) => {
        // update local storage to reflect selected app

        Storage.setDefaultAppIdParamInGroupId(appGroup.id, appIdParam);
        Storage.setDefaultAppIdParamInOrg(props.orgId, appIdParam);

        history.push(appLink);
    };

    const renderAppsInGroup = () => {
        const appGroups = appsInGroup.map((app, index) => {
            const appLink = createAppRoute(props.orgIdParam, app.appId);
            const isSelected = app.appId === props.selectedAppIdParam;
            const needsMargin = index !== 0;
            const isDisabledTreatment = app.isRestrictedApp;
            const baseStyles: React.CSSProperties = {
                cursor: 'pointer',
            };
            const additionalStyles = needsMargin ? { marginTop: 6 } : {};

            let contentNode = null;
            let containerStyles: React.CSSProperties = {
                ...baseStyles,
                ...additionalStyles,
            };

            var shouldHideOverlay = !app.appSettings.shouldShowBetaOverlayOnWorkflowsBetaOrgs;

            if (useAppImageUrlForApp[app.id]) {
                contentNode = (
                    <div style={containerStyles}>
                        <AvatarLink
                            square
                            isSelected={isSelected}
                            path={appLink}
                            letter={app.appSettings.appName.charAt(0)}
                            imageUrl={app.appSettings.appImageUrl}
                            appPlatform={app.appSettings.platform}
                            appStoreType={app.appSettings.appStoreType}
                            isDisabledTreatment={isDisabledTreatment}
                            size={34}
                            hideOverlay={shouldHideOverlay}
                            shouldShowBetaOverlayOnWorkflowsBetaOrgs={
                                app.appSettings.shouldShowBetaOverlayOnWorkflowsBetaOrgs
                            }
                        />
                    </div>
                );
            } else {
                containerStyles = {
                    ...containerStyles,
                    paddingLeft: 24,
                    paddingRight: 24,
                };

                let appIcon = null;

                switch (app.appSettings.platform) {
                    case AppPlatform.TvOS:
                        appIcon = (
                            <TvOSIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                    case AppPlatform.VisionOS:
                        appIcon = (
                            <VisionOSIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                    case AppPlatform.AndroidTV:
                        appIcon = (
                            <AndroidTVIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                    case AppPlatform.IosSDK:
                    case AppPlatform.Ios:
                        appIcon = (
                            <AppleAppIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                    case AppPlatform.WearOS:
                        appIcon = (
                            <WearOSIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                    case AppPlatform.AndroidSDK:
                    case AppPlatform.Android:
                        appIcon = (
                            <AndroidAppIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                appStoreType={app.appSettings.appStoreType}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                    case AppPlatform.ReactNativeOTA:
                        appIcon = (
                            <ReactNativeAppIcon
                                isSelected={isSelected}
                                isDisabledTreatment={isDisabledTreatment}
                                width={34}
                                height={34}
                            />
                        );
                        break;
                }

                contentNode = (
                    <div style={containerStyles} onClick={() => navigateToAppLink(app.appId, appLink)}>
                        {appIcon}
                    </div>
                );
            }

            return (
                <Tooltip
                    title={`${app.appSettings.appName} (${displayTextForAppPlatform(app.appSettings.platform)})`}
                    key={app.id}
                    placement="right"
                    mouseEnterDelay={0.3}
                >
                    {contentNode}
                </Tooltip>
            );
        });

        return appGroups;
    };

    const onClickAppGroup = (firstAppInGroup: AppEntity) => {
        if (!showAllApps) {
            // only open tray if it's current closed

            if (!appGroupContainsSelectedApp) {
                // navigating to a different app group from currently selected

                // see if we have a cached default app for the app group
                const preferredDefaultAppId = Storage.getDefaultAppIdInGroupId(appGroup.id);

                // check that default app in app group still belongs to the clicked app group
                const defaultAppInAppGroup = appsInGroup.find((app) => app.appId === preferredDefaultAppId);

                // if preferred default app exists, navigate to that
                // otherwise, navigate to first app in group if nothing stored

                const appIdParam =
                    preferredDefaultAppId && !!defaultAppInAppGroup ? preferredDefaultAppId : firstAppInGroup?.appId;

                if (!appIdParam) {
                    return;
                }

                const appPath = createAppRoute(props.orgIdParam, appIdParam);

                history.push(appPath);

                Storage.setDefaultAppIdParamInOrg(props.orgId, appIdParam);
            }
        }
    };

    React.useEffect(() => {
        setShowAllApps(appGroupContainsSelectedApp);
    }, [appGroupContainsSelectedApp]);

    if (hasMultipleAppsInGroup) {
        const appGroupImage = appGroup.appGroupImageUrl;
        const firstLetter = appGroup.name.charAt(0).toUpperCase();
        const firstAppInGroup = appsInGroup[0];
        const numAppsInGroup = appsInGroup.length;

        const duration = 60;

        const defaultStyle: React.CSSProperties = {
            transition: `height ${duration}ms ease-in-out, padding-bottom ${duration}ms ease-in-out, padding-top ${duration}ms ease-in-out`,
            height: 0,
            paddingBottom: 0,
            paddingTop: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'hidden',
        };

        const padding = 10;
        const containerHeight = numAppsInGroup * 34 + (numAppsInGroup - 1) * 6 + 2 * padding;

        const transitionStyles = {
            entering: { height: 0, paddingBottom: 0 },
            entered: { height: containerHeight, paddingBottom: padding, paddingTop: padding },
            exiting: { height: containerHeight, paddingBottom: padding, paddingTop: padding },
            exited: { height: 0, paddingBottom: 0 },
        };

        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: antGrey.grey8,
                        width: 48,
                        borderRadius: 8,
                    }}
                >
                    <Tooltip title={appGroup.name} placement="right" mouseEnterDelay={0.3}>
                        <div
                            onClick={() => onClickAppGroup(firstAppInGroup)}
                            style={appGroupContainsSelectedApp ? styles.avatarSelected : styles.avatarUnselected}
                        >
                            <Avatar shape="square" size={48} src={appGroupImage}>
                                {firstLetter}
                            </Avatar>
                            {isSDKGroup && (
                                <img
                                    src={sdkOverlayImage}
                                    style={{ width: 48, height: 48, position: 'absolute', top: 0, left: 16 }}
                                />
                            )}
                        </div>
                    </Tooltip>
                    <Transition in={showAllApps} timeout={20}>
                        {(state) => (
                            <div
                                style={{
                                    ...defaultStyle,
                                    ...transitionStyles[state],
                                }}
                            >
                                {renderAppsInGroup()}
                            </div>
                        )}
                    </Transition>
                </div>
            </div>
        );
    } else {
        const app = appsInGroup[0];
        if (!app) {
            return null;
        }
        const appIdParam = app.appId;
        const path = createAppRoute(props.orgIdParam, appIdParam);
        const letter = app.appSettings.appName.charAt(0).toUpperCase();

        return (
            <Tooltip title={appGroup.name} placement="right" mouseEnterDelay={0.3}>
                <div onClick={() => Storage.setDefaultAppIdParamInOrg(props.orgIdParam, appIdParam)}>
                    <AvatarLink
                        key={app.id}
                        isSelected={appGroupContainsSelectedApp}
                        isDisabledTreatment={app.isRestrictedApp}
                        path={path}
                        letter={letter}
                        square={true}
                        imageUrl={app.appSettings.appImageUrl}
                        appPlatform={app.appSettings.platform}
                        appStoreType={app.appSettings.appStoreType}
                        shouldShowBetaOverlayOnWorkflowsBetaOrgs={
                            app.appSettings.shouldShowBetaOverlayOnWorkflowsBetaOrgs
                        }
                    />
                </div>
            </Tooltip>
        );
    }
};

export default AppGroupIconComponent;
